// The app shell: the `AppShell` of the PMS design system, as it is (56 px rail
// with the active pill on the full height, 56 px header with the official logo,
// full-screen menu under 768 px, skip link and `main` target, profile menu with
// the logout). No styling lives here: it only plugs the router, the role-based
// entries and the logout into the package.
import { meApi } from '@/lib/api';
import { useEffectiveRole } from '@/lib/effective-role';
import { type LandingTarget, resolveLanding } from '@/lib/landing';
import { renderRouterLink } from '@/lib/render-link';
import { useLogout } from '@/lib/use-logout';
import { AppShell, type NavItem } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';

function landingHref(target: LandingTarget): string {
  return target.to === '/' ? '/' : `/companies/${target.params.companyId}`;
}

/** « Alice Pesty » → « AP » ; a single word gives one letter. */
function initialsOf(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

const ADMIN_PATHS = ['/admin/claude-credentials', '/admin/docs-data', '/settings'];

interface MatricesShellProps {
  children: ReactNode;
}

/**
 * Persistent top-level navigation. Home routes by role via the same
 * `resolveLanding` the `/` loader uses: `pms_user`/`pms_admin` (global roles)
 * go to the company grid, `user`/`admin` (company-scoped) go straight to their
 * own company space. Identifiants Claude and Documentation technique are both
 * `pms_admin`-only, hidden entirely (not shown-disabled) for everyone else;
 * Réglages is for the PMS team (`pms_user`, `pms_admin`).
 */
export function MatricesShell({ children }: MatricesShellProps) {
  const { pathname } = useLocation();
  const logout = useLogout();
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => meApi.get() });
  // Only gates the two pms_admin-only entries. Landing resolution and the
  // membership query's `enabled` condition keep reading the real `user?.role`.
  const effectiveRole = useEffectiveRole(user);
  const { data: membership } = useQuery({
    queryKey: ['me', 'company'],
    queryFn: () => meApi.getMembership(),
    enabled: user?.role === 'user' || user?.role === 'admin',
  });

  const target: LandingTarget = user
    ? resolveLanding(user, membership?.companyId ?? null)
    : { to: '/' };
  const homeHref = landingHref(target);

  const nav: NavItem[] = [
    {
      id: 'home',
      label: 'Accueil',
      icon: 'home',
      href: homeHref,
      active: !ADMIN_PATHS.some((path) => pathname.startsWith(path)),
    },
  ];
  if (effectiveRole === 'pms_admin') {
    nav.push(
      {
        id: 'claude-credentials',
        label: 'ID',
        icon: 'key',
        href: '/admin/claude-credentials',
        placement: 'bottom',
        active: pathname.startsWith('/admin/claude-credentials'),
      },
      {
        id: 'docs-data',
        label: 'Doc',
        icon: 'menu_book',
        href: '/admin/docs-data',
        placement: 'bottom',
        active: pathname.startsWith('/admin/docs-data'),
      },
    );
  }
  if (user?.role === 'pms_user' || user?.role === 'pms_admin') {
    nav.push({
      id: 'settings',
      label: 'Réglages',
      icon: 'settings',
      href: '/settings',
      placement: 'bottom',
      active: pathname.startsWith('/settings'),
    });
  }

  return (
    <AppShell
      nav={nav}
      profile={{
        label: 'Mon profil',
        ...(user ? { identifier: user.displayName, initials: initialsOf(user.displayName) } : {}),
        onSignOut: () => void logout(),
      }}
      homeHref={homeHref}
      logoLabel="Matrices, accueil"
      renderLink={renderRouterLink}
    >
      {children}
    </AppShell>
  );
}
